import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useAuthStore } from '@/features/auth';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import { UserAvatar } from './UserAvatar';
import { SettingsIcon, LogOutIcon, InfoIcon, ChevronDownIcon, GlobeIcon, ShieldCheck } from 'lucide-react';
import { toast } from '@/components/CustomToast';
import { api } from '@/services/api';
import { useRTL } from '@/shared/hooks/useRTL';

/**
 * UserMenu Component
 * Dropdown menu with user avatar, name, and action items
 * Uses Headless UI for accessibility
 */
export const UserMenu = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('common');
  const { user, clearAuth, isAuthenticated } = useAuthStore();
  const { canAccessAdminSection } = usePermissions();
  const [isChangingLanguage, setIsChangingLanguage] = useState(false);
  const isRTL = useRTL();

  const currentLanguage = i18n.language === 'he' ? 'עברית' : 'English';

  // Navigate to login when user logs out
  useEffect(() => {
    if (!isAuthenticated) {
      // Small delay to ensure state is fully persisted
      const timer = setTimeout(() => {
        navigate('/login', { replace: true });
      }, 100);
      return () => {
        clearTimeout(timer);
      };
    }
  }, [isAuthenticated, navigate]);

  if (!user) return null;

  const handleLogout = () => {
    clearAuth();
    toast.success(t('auth:logout.success'));
    // Navigation happens via useEffect after state is cleared
  };

  const handleLanguageChange = async () => {
    const newLanguage = i18n.language === 'he' ? 'en' : 'he';
    const newDir = newLanguage === 'he' ? 'rtl' : 'ltr';

    setIsChangingLanguage(true);

    try {
      // Update language in i18n
      await i18n.changeLanguage(newLanguage);

      // Update document direction
      document.documentElement.dir = newDir;
      document.documentElement.lang = newLanguage;

      // Persist to database
      await api.patch('/users/me/language', { preferredLanguage: newLanguage });

      toast.success(
        newLanguage === 'he'
          ? 'שפה עודכנה בהצלחה'
          : 'Language updated successfully'
      );
    } catch (error) {
      console.error('Failed to change language:', error);
      toast.error(
        i18n.language === 'he'
          ? 'שגיאה בעדכון שפה'
          : 'Failed to update language'
      );
    } finally {
      setIsChangingLanguage(false);
    }
  };

  return (
    <>

      <Menu as="div" className="relative inline-block text-left" data-testid="user-menu">
        <div className="relative">
          {/* User Menu Button - Circular Frame with Avatar Only */}
          <MenuButton
            className="typo-navigation neo-icon-btn flex items-center justify-center overflow-hidden rounded-full"
            style={{
              width: '48px',
              height: '48px',
              backgroundColor: 'var(--theme-surface)',
              color: 'var(--theme-text)',
              padding: 0,
            }}
          >
            <UserAvatar user={user} size="lg" className="!border-0" />
          </MenuButton>

          {/* Small Chevron Indicator - Outside the button */}
          <div
            className="neo-icon-btn absolute flex items-center justify-center pointer-events-none rounded-full"
            style={{
              bottom: '0px',
              ...(isRTL ? { left: '0px' } : { right: '0px' }),
              width: '18px',
              height: '18px',
              backgroundColor: 'var(--theme-card-yellow)',
            }}
          >
            <ChevronDownIcon className="h-3 w-3" style={{ color: 'var(--theme-text)' }} />
          </div>
        </div>

        {/* Neobrutalism Dropdown Menu */}
        <MenuItems
          modal={false}
          className={`neo-dropdown-menu absolute ${isRTL ? 'left-0 origin-top-left' : 'right-0 origin-top-right'} z-50 mt-2 w-56 focus:outline-none transition-all duration-200`}
        style={{
          backgroundColor: 'var(--theme-card-yellow)',
        }}
        transition
      >
        {/* User Info Section - Neobrutalism Header */}
        <div
          className="px-4 py-3 border-b-2 border-black"
          style={{
            backgroundColor: 'var(--theme-card-cyan)',
          }}
        >
          <p
            className="text-sm font-bold uppercase"
            style={{
              color: 'var(--theme-text)',
            }}
          >
            {user.firstName} {user.lastName}
          </p>
          <p className="text-xs truncate mt-1" style={{ color: 'var(--theme-text-muted)' }}>
            {user.email}
          </p>
        </div>

        {/* Neobrutalism Menu Items */}
        <div className="py-2 px-2">
          <MenuItem>
            {({ focus }) => (
              <button
                onClick={handleLanguageChange}
                disabled={isChangingLanguage}
                className={`group flex w-full items-center gap-3 px-4 py-2 text-sm font-bold uppercase ${focus ? '' : ''}`}
                style={{
                  backgroundColor: focus ? 'var(--theme-card-cyan)' : 'transparent',
                  color: 'var(--theme-text)',
                  border: focus ? '2px solid var(--theme-border)' : '2px solid transparent',
                  boxShadow: focus ? '3px 3px 0px var(--theme-border)' : 'none',
                  marginBottom: '4px',
                  opacity: isChangingLanguage ? 0.5 : 1,
                }}
              >
                <GlobeIcon className="h-4 w-4" />
                <span>{currentLanguage}</span>
              </button>
            )}
          </MenuItem>

          <MenuItem>
            {({ focus }) => (
              <button
                onClick={() => navigate('/settings')}
                className={`group flex w-full items-center gap-3 px-4 py-2 text-sm font-bold uppercase ${focus ? '' : ''}`}
                style={{
                  backgroundColor: focus ? 'var(--theme-card-purple)' : 'transparent',
                  color: 'var(--theme-text)',
                  border: focus ? '2px solid var(--theme-border)' : '2px solid transparent',
                  boxShadow: focus ? '3px 3px 0px var(--theme-border)' : 'none',
                  marginBottom: '4px',
                }}
              >
                <SettingsIcon className="h-4 w-4" />
                <span>{t('userMenu.settings')}</span>
              </button>
            )}
          </MenuItem>

          {/* Admin Menu Item - Visible to OPERATOR+ */}
          {canAccessAdminSection() && (
            <MenuItem>
              {({ focus }) => (
                <button
                  onClick={() => navigate('/admin')}
                  className={`group flex w-full items-center gap-3 px-4 py-2 text-sm font-bold uppercase ${focus ? '' : ''}`}
                  style={{
                    backgroundColor: focus ? 'var(--theme-card-orange)' : 'transparent',
                    color: 'var(--theme-text)',
                    border: focus ? '2px solid var(--theme-border)' : '2px solid transparent',
                    boxShadow: focus ? '3px 3px 0px var(--theme-border)' : 'none',
                    marginBottom: '4px',
                  }}
                >
                  <ShieldCheck className="h-4 w-4" />
                  <span>{t('navigation.admin')}</span>
                </button>
              )}
            </MenuItem>
          )}

          <MenuItem>
            {({ focus }) => (
              <button
                onClick={() => navigate('/about')}
                className={`group flex w-full items-center gap-3 px-4 py-2 text-sm font-bold uppercase ${focus ? '' : ''}`}
                style={{
                  backgroundColor: focus ? 'var(--theme-card-green)' : 'transparent',
                  color: 'var(--theme-text)',
                  border: focus ? '2px solid var(--theme-border)' : '2px solid transparent',
                  boxShadow: focus ? '3px 3px 0px var(--theme-border)' : 'none',
                }}
              >
                <InfoIcon className="h-4 w-4" />
                <span>{t('userMenu.about')}</span>
              </button>
            )}
          </MenuItem>
        </div>

        {/* Neobrutalism Logout Section */}
        <div
          className="py-2 px-2"
          style={{
            borderTop: '3px solid var(--theme-border)',
          }}
        >
          <MenuItem>
            {({ focus }) => (
              <button
                onClick={handleLogout}
                data-testid="logout-button"
                className={`group flex w-full items-center gap-3 px-4 py-2 text-sm font-bold uppercase ${focus ? '' : ''}`}
                style={{
                  backgroundColor: focus ? 'var(--theme-card-pink)' : 'transparent',
                  color: 'var(--theme-text)',
                  border: focus ? '2px solid var(--theme-border)' : '2px solid transparent',
                  boxShadow: focus ? '3px 3px 0px var(--theme-border)' : 'none',
                }}
              >
                <LogOutIcon className="h-4 w-4" />
                <span>{t('userMenu.logout')}</span>
              </button>
            )}
          </MenuItem>
        </div>
      </MenuItems>
    </Menu>
    </>
  );
};
